import React, { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';
import TransitionScreen from 'common/components/TransitionScreen';
import { ErrorActions } from 'common/constants/actions';
import { useNavigate } from 'react-router-dom';
import userContextSlice from 'common/slices/userContextSlice';
import contentSharingSlice from 'areas/content-sharing/contentSharingSlice';
import { emptySplitApi } from 'store/emptyApi';
import { clearStorageExceptUserPreferences } from 'common/utils/userStorage';
import { endImpersonationSession } from 'common/api/impersonationApi';
import { getImpersonationSessionId } from 'common/slices/impersonationSlice';

const Logout = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { signOut } = useAuthenticator();
  const navigate = useNavigate();
  const hasLoggedOut = useRef(false);

  useEffect(() => {
    if (hasLoggedOut.current) {
      return;
    }
    hasLoggedOut.current = true;

    const performLogout = async () => {
      const isTestMode =
        process.env.NODE_ENV === 'development' &&
        (localStorage.getItem('test_jwt_token') || localStorage.getItem('cognito_token'));

      // Impersonation is tied to the session — when you log out, it ends.
      // Best-effort: failure here must not block logout. Defense-in-depth on
      // the backend (auth_time binding + TTL) catches the case where this
      // call doesn't run.
      const sessionId = getImpersonationSessionId(store.getState());
      if (sessionId != null) {
        try {
          await endImpersonationSession(sessionId);
        } catch (error) {
          console.warn('Failed to end impersonation session during logout:', error);
        }
      }

      try {
        if (!isTestMode) {
          await signOut();
        }
      } catch (error) {
        console.error('Error during logout:', error);
      } finally {
        dispatch(userContextSlice.actions.clearUserContext());
        dispatch(contentSharingSlice.actions.clearToken());
        dispatch(emptySplitApi.util.resetApiState());
        dispatch({
          type: ErrorActions.CLEAR_ALL,
        });

        clearStorageExceptUserPreferences();
        sessionStorage.clear();
        navigate('/login');
      }
    };

    performLogout();
  }, [dispatch, store, signOut, navigate]);

  return <TransitionScreen message="Logging you out..." signOut={signOut} />;
};

export default Logout;
